import React from "react";
import { useInView } from "react-intersection-observer";
import "./AboutPage.css";
import usePageMeta from "../../hooks/usePageMeta";

// A reusable component to handle the scroll animation
const AnimatedSection = ({ children }) => {
  const { ref, inView } = useInView({
    triggerOnce: true, // Animation triggers only once
    threshold: 0.1, // Trigger when 10% of the element is visible
  });

  return (
    <section
      ref={ref}
      className={`about-section ${inView ? "is-visible" : ""}`}
    >
      {children}
    </section>
  );
};

function AboutPage() {
  usePageMeta(
    "About | TjWit Media",
    "Learn the story behind Tyler Witkowski and TjWit Media, from high school sports to the University of Tampa's Athletics Department."
  );
  return (
    <>
      {/* Background container for floating images */}
      <div className="floating-images-bg">
        <img
          src="/sports1.jpg"
          alt="Floating sports action shot 1"
          className="floating-image float-1"
        />
        <img
          src="/sports2.jpg"
          alt="Floating sports action shot 2"
          className="floating-image float-2"
        />
        <img
          src="/sports3.jpg"
          alt="Floating sports action shot 3"
          className="floating-image float-3"
        />
      </div>

      <div className="about-page-container">
        {/* --- Intro Section --- */}
        <AnimatedSection>
          <div className="intro-content">
            <img
              src="/profile-pic.jpg"
              alt="Headshot of Tyler Witkowski"
              className="about-image"
            />
            <h1>Hello, I'm Tyler Witkowski</h1>
            <p className="subtitle">
              Welcome to my journey as a photographer and the story behind TjWit
              Media.
            </p>
          </div>
        </AnimatedSection>

        {/* --- High School Journey Section --- */}
        <AnimatedSection>
          <h2>The Spark</h2>
          <p>
            During my first few years at Priory, I attended “Raven Roundup,” an
            event showcasing school clubs. That all changed on March 22nd, 2022,
            when I attended the Priory Ravens' Wickenheiser Cup finals and
            decided to take pictures at the game. From that moment on, my
            journey as a photographer began.
          </p>
          <p>
            Fast forward to 2023, I founded the Priory Sports Media Club,
            mentoring underclassmen with a shared passion for sports media. This
            club evolved to create and manage Priory's Athletics Instagram
            account, posting game day graphics, final score updates, and event
            photos.
          </p>
        </AnimatedSection>

        {/* --- Timeline Section --- */}
        <AnimatedSection>
          <h2>Milestones</h2>
          <div className="timeline">
            <div className="timeline-item">
              <strong>March 2022:</strong> Took my first sports photos at the
              Wickenheiser Cup.
            </div>
            <div className="timeline-item">
              <strong>November 2022:</strong> Created the @prioryphotos
              Instagram page.
            </div>
            <div className="timeline-item">
              <strong>September 2023:</strong> Founded the Sports Media Club.
            </div>
            <div className="timeline-item">
              <strong>November 2023:</strong> Created the @stlprioryathletics
              Instagram page.
            </div>
            <div className="timeline-item">
              <strong>2024:</strong> Attended 84 sporting events & founded TjWit
              Media.
            </div>
          </div>
        </AnimatedSection>

        {/* --- College Journey Section --- */}
        <AnimatedSection>
          <h2>The Next Chapter: University of Tampa</h2>
          <p>
            Continuing my passion at the collegiate level, I joined the staff of
            UT's student-run newspaper, The Minaret, as a staff photographer.
            This role quickly expanded my portfolio beyond the sports field into
            news and event coverage.
          </p>
          <p>
            Shortly after, I was hired by the University of Tampa's Athletics
            Department. As a member of their creative team, I now produce
            professional sports photography and graphic design content,
            contributing to the visual identity of the Spartans.
          </p>
        </AnimatedSection>

        {/* --- Closing Section --- */}
        <AnimatedSection>
          <h2>My Mission</h2>
          <p>
            Photography is more than just a hobby for me; it's a way to capture
            raw emotions and tell unforgettable stories. With TjWit Media, I
            hope to continue this journey and inspire others to find and pursue
            their passions with the same dedication.
          </p>
        </AnimatedSection>
      </div>
    </>
  );
}

export default AboutPage;
